import React, { useEffect, useSyncExternalStore } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './lib/theme-context';
import { useNotifications } from './hooks/useNotifications';
import { NotificationContext } from './hooks/useNotificationContext';
import { setNotifyFunction, useChatStore } from './stores/useChatStore';
import { Toaster } from 'react-hot-toast';
import { ErrorBoundary } from './components/ErrorBoundary';
import { initializeGlobalErrorCapturing } from './utils/globalErrorSetup';
import { DemoModeProvider } from './contexts/DemoModeContext';
import { NativeCommsRBACProvider } from './contexts/NativeCommsRBAC';
import { ClientIsolationProvider } from './contexts/ClientIsolationContext';
import { clientIsolationService } from './services/ClientIsolationService';
import './styles/interaction-framework.css';
import BeehiveStartPage from './pages/beehive/start';
import BeehiveWorkspacePage from './pages/beehive/workspace';
import KnowledgeBasePage from './pages/beehive/knowledge';
import SettingsPage from './pages/beehive/settings';
import BeehiveAdminDashboard from './pages/beehive/admin/dashboard';
import MetricsPage from './pages/beehive/admin/metrics';
import AgentsPage from './pages/beehive/admin/agents';
import EmergencyPage from './pages/beehive/admin/emergency';
import ActivityPage from './pages/beehive/admin/activity';
import TasksPage from './pages/beehive/admin/tasks';
import InternalDashboardPage from './pages/beehive/internal';
import SecurityPanel from './components/Admin/SecurityPanel';
import OperationsPanel from './components/Admin/OperationsPanel';
import { AdminAuthGuard } from './components/Beehive/AdminAuthGuard';
import DashboardLayout from './components/DashboardLayout';
import { usePluginRegistry } from './plugins/registry';
import BranchGuardPanel from './components/Beehive/BranchGuardPanel';
import SupervisorPanel from './components/Beehive/SupervisorPanel';
import ArcGitPanel from './components/Beehive/ArcGitPanel';
import RepoStatsPanel from './components/Beehive/RepoStatsPanel';
import SchedulerPanel from './components/Beehive/SchedulerPanel';
import PlaybooksPanel from './components/Beehive/PlaybooksPanel';
import AutoFixPanel from './components/Beehive/AutoFixPanel';
import DataExportPanel from './components/Beehive/DataExportPanel';
import NotificationPanel from './components/Beehive/NotificationPanel';
import AnnotationPanel from './components/Beehive/AnnotationPanel';
import ResiliencePanel from './components/Beehive/ResiliencePanel';
import ArcOversightPlugin from './plugins/arcOversight';
import { AssistantPanel } from './components/Beehive/AssistantPanel';
import SupportBridgeDemo from './pages/SupportBridgeDemo';
import RealSignalMapPage from '@/pages/realsignal/MapPage';
// import SupportBridgeApp from '@src/support-bridge/App';
import { ControlBridgePanel } from './components/ControlBridge/ControlBridgePanel';
import EngineeringBridgePage from './pages/EngineeringBridgePage';
import { ProjectPicker } from './components/ControlBridge/OnboardingHub/ProjectPicker';
import OnboardingHub from './components/ControlBridge/OnboardingHub/OnboardingHub';
import ProjectsList from './components/ControlHub/Projects/ProjectsList';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import TimingTestPage from './pages/beehive/timing-test';
import ScheduleLink from './pages/share/ScheduleLink';
import SmartGrid from './pages/hivecore/smartgrid';
import EnhancedSmartGridPage from './pages/hivecore/enhanced-smartgrid';
import WealthOSLayout from './layouts/WealthOSLayout';
import WealthOSDashboard from './pages/wealthos/dashboard';
import WealthOSSmartGrid from './pages/wealthos/smartgrid';
import WealthOSReports from './pages/wealthos/reports';
import WealthOSForms from './pages/wealthos/forms';
import WealthOSOversight from './pages/wealthos/oversight';
import WealthOSSettings from './pages/wealthos/settings';
import { NotificationsAdminPanel } from './components/ControlBridge/Notifications/AdminPanel';
import FitnessDemo from './pages/fitness-demo';
import FitnessMobile from './pages/fitness-mobile';
import FitnessDesktop from './pages/fitness-desktop';
import RealSignalApp from './pages/realsignal';
import HiveCoreMobile from './pages/mobile/index';
import HiveCoreControlPage from './pages/hivecore/control';
import HiveCoreWorkstationPage from './pages/hivecore/workstation';
import { HiveCoreProvider } from './services/hivecore-scene-stream/HiveCoreProvider';
import { setAbdaMode } from './runtime/abdaRuntime';
import { FLAGS, onFlagsChange, getFlags } from './config/flags';

const OnboardingProjectPicker: React.FC = () => {
    const navigate = useNavigate();

    const handleProjectSelect = (project: any) => {
        navigate(`/beehive/control-bridge/onboarding/${project.id}`);
    };

    return <ProjectPicker onProjectSelect={handleProjectSelect} />;
};

const OnboardingHubWrapper: React.FC = () => {
    const { projectId } = useParams<{ projectId: string }>();
    const navigate = useNavigate();

    React.useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('demo') === '1') {
            console.log('Demo mode enabled');
        }
    }, []);

    if (!projectId) {
        navigate('/beehive/control-bridge/onboarding');
        return null;
    }

    return <OnboardingHub />;
};

const MissingApiBasePage: React.FC = () => {
    return (
        <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center">
            <div className="max-w-lg space-y-4 rounded border border-slate-800 bg-slate-900/40 p-6">
                <h1 className="text-xl font-semibold">Missing env var</h1>
                <p className="text-sm text-slate-300">
                    VITE_HIVECORE_API_URL must be set for this deployment.
                </p>
                <p className="text-xs text-slate-400">
                    Set the backend base URL in your hosting environment and redeploy.
                </p>
            </div>
        </div>
    );
};

const HiveCoreDesktopRoute: React.FC = () => {
    setAbdaMode(true);

    React.useEffect(() => {
        return () => setAbdaMode(false);
    }, []);

    return (
        <HiveCoreProvider
            baseUrl={import.meta.env.VITE_HIVECORE_API_URL || ''}
            jwt={import.meta.env.VITE_HIVECORE_JWT || 'DEV_JWT_FOR_TESTING'}
            sceneId="hivecore.desktop"
            profile="desktop"
        >
            <BeehiveWorkspacePage abdaMode={true} />
        </HiveCoreProvider>
    );
};

const HiveCoreMobileRoute: React.FC = () => {
    setAbdaMode(true);

    React.useEffect(() => {
        return () => setAbdaMode(false);
    }, []);

    return (
        <HiveCoreProvider
            baseUrl={import.meta.env.VITE_HIVECORE_API_URL || ''}
            jwt={import.meta.env.VITE_HIVECORE_JWT || 'DEV_JWT_FOR_TESTING'}
            sceneId="hivecore.mobile"
            profile="mobile"
        >
            <BeehiveWorkspacePage abdaMode={true} />
        </HiveCoreProvider>
    );
};

const HiveCoreControlRoute: React.FC = () => {
    setAbdaMode(true);

    React.useEffect(() => {
        return () => setAbdaMode(false);
    }, []);

    return (
        <HiveCoreProvider
            baseUrl={import.meta.env.VITE_HIVECORE_API_URL || ''}
            jwt={import.meta.env.VITE_HIVECORE_JWT || 'DEV_JWT_FOR_TESTING'}
            sceneId="hivecore.control"
            profile="desktop"
        >
            <HiveCoreControlPage />
        </HiveCoreProvider>
    );
};

/**
 * Redirect component that preserves query parameters.
 * Used for deprecating legacy routes while maintaining deep link functionality.
 */
const RedirectWithQueryParams: React.FC<{ to: string }> = ({ to }) => {
    const location = useLocation();
    // Preserve query string when redirecting
    const targetUrl = location.search ? `${to}${location.search}` : to;
    return <Navigate to={targetUrl} replace />;
};

// Lazy load HiveCoreStudio outside component to avoid recreating on every render
const HiveCoreStudioLazy = React.lazy(() => import('./pages/hivecore/studio-v2'));

const HiveCoreStudioRoute: React.FC = () => {
    // Extract appId to key on it - only remount when appId changes (system page navigation)
    // This avoids remounting on every pathname change which would tear down Socket.IO connections
    const { appId } = useParams<{ appId?: string }>();
    setAbdaMode(true);

    React.useEffect(() => {
        return () => setAbdaMode(false);
    }, []);

    return (
        <HiveCoreProvider
            baseUrl={import.meta.env.VITE_HIVECORE_API_URL || ''}
            jwt={import.meta.env.VITE_HIVECORE_JWT || 'DEV_JWT_FOR_TESTING'}
            sceneId="hivecore.studio"
            profile="desktop"
        >
            <React.Suspense
                fallback={
                    <div style={{ color: 'white', padding: '20px' }}>
                        Loading HiveCore Studio...
                    </div>
                }
            >
                {/* Key on appId to force re-render only when navigating to/from system pages */}
                <HiveCoreStudioLazy key={appId || 'default'} />
            </React.Suspense>
        </HiveCoreProvider>
    );
};

function App() {
    const apiBase = import.meta.env.VITE_HIVECORE_API_URL;
    const flags = useSyncExternalStore(onFlagsChange, getFlags, getFlags);
    const legacyEnabled = flags.ENABLE_LEGACY;
    const notifications = useNotifications();
    const initializeWebhookListeners = useChatStore(state => state.initializeWebhookListeners);
    const pluginRegistry = usePluginRegistry();
    const didInitAppRef = React.useRef(false);
    const missingApiBase = !apiBase && !import.meta.env.DEV;

    if (missingApiBase) {
        return <MissingApiBasePage />;
    }

    useEffect(() => {
        if (didInitAppRef.current) return;
        didInitAppRef.current = true;

        setNotifyFunction(notifications.notify);
        initializeWebhookListeners();
        initializeGlobalErrorCapturing();

        const initializeClientIsolation = () => {
            try {
                const userId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

                clientIsolationService.initialize({
                    clientId: `client_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                    tenantId: 'sculptd_default',
                    userId,
                    sessionId,
                    autoGenerate: true,
                    persistInStorage: true,
                    encryptionEnabled: true,
                });

                console.log('🔒 Client isolation initialized for SCULPTD APP');
            } catch (error) {
                console.error('❌ Failed to initialize client isolation:', error);
            }
        };

        initializeClientIsolation();
    }, []);

    return (
        <ErrorBoundary>
            <ThemeProvider>
                <DemoModeProvider>
                    <ClientIsolationProvider>
                        <NotificationContext.Provider value={notifications}>
                            <Router>
                                <Routes>
                                    <Route
                                        path="/"
                                        element={
                                            legacyEnabled ? (
                                                window.location.hostname === 'sculptd.app' ||
                                                window.location.hostname === 'www.sculptd.app' ? (
                                                    <Navigate to="/fitness-mobile" replace />
                                                ) : window.location.hostname === 'realsignal.app' ||
                                                  window.location.hostname ===
                                                      'www.realsignal.app' ? (
                                                    <Navigate to="/realsignal" replace />
                                                ) : (
                                                    <Navigate to="/hivecore/studio-v2" replace />
                                                )
                                            ) : (
                                                <Navigate to="/hivecore/workstation" replace />
                                            )
                                        }
                                    />
                                    <Route path="/realsignal/map" element={<RealSignalMapPage />} />
                                    <Route
                                        path="/hivecore/workstation"
                                        element={<HiveCoreWorkstationPage />}
                                    />

                                    {legacyEnabled && (
                                        <>
                                            <Route
                                                path="/beehive"
                                                element={
                                                    <Navigate to="/hivecore/studio-v2" replace />
                                                }
                                            />
                                            <Route
                                                path="/beehive/start"
                                                element={<BeehiveStartPage />}
                                            />
                                            {/* Legacy workspace route - redirect to Studio v3.6 (preserves query params) */}
                                            <Route
                                                path="/beehive/workspace"
                                                element={
                                                    <RedirectWithQueryParams to="/hivecore/studio-v2" />
                                                }
                                            />
                                            <Route
                                                path="/beehive/knowledge"
                                                element={<KnowledgeBasePage />}
                                            />
                                            <Route
                                                path="/beehive/settings"
                                                element={<SettingsPage />}
                                            />
                                            <Route
                                                path="/beehive/admin"
                                                element={
                                                    <AdminAuthGuard>
                                                        <BeehiveAdminDashboard />
                                                    </AdminAuthGuard>
                                                }
                                            />
                                            <Route
                                                path="/beehive/admin/dashboard"
                                                element={
                                                    <AdminAuthGuard>
                                                        <BeehiveAdminDashboard />
                                                    </AdminAuthGuard>
                                                }
                                            />
                                            <Route
                                                path="/beehive/admin/metrics"
                                                element={
                                                    <AdminAuthGuard>
                                                        <MetricsPage />
                                                    </AdminAuthGuard>
                                                }
                                            />
                                            <Route
                                                path="/beehive/admin/agents"
                                                element={
                                                    <AdminAuthGuard>
                                                        <AgentsPage />
                                                    </AdminAuthGuard>
                                                }
                                            />
                                            <Route
                                                path="/beehive/admin/emergency"
                                                element={
                                                    <AdminAuthGuard>
                                                        <EmergencyPage />
                                                    </AdminAuthGuard>
                                                }
                                            />
                                            <Route
                                                path="/beehive/admin/activity"
                                                element={
                                                    <AdminAuthGuard>
                                                        <ActivityPage />
                                                    </AdminAuthGuard>
                                                }
                                            />
                                            <Route
                                                path="/beehive/admin/security"
                                                element={
                                                    <AdminAuthGuard>
                                                        <SecurityPanel />
                                                    </AdminAuthGuard>
                                                }
                                            />
                                            <Route
                                                path="/beehive/admin/resilience"
                                                element={
                                                    <AdminAuthGuard>
                                                        <ResiliencePanel />
                                                    </AdminAuthGuard>
                                                }
                                            />
                                            <Route
                                                path="/beehive/admin/tasks"
                                                element={
                                                    <AdminAuthGuard>
                                                        <TasksPage />
                                                    </AdminAuthGuard>
                                                }
                                            />
                                            <Route
                                                path="/beehive/admin/operations"
                                                element={
                                                    <AdminAuthGuard>
                                                        <OperationsPanel />
                                                    </AdminAuthGuard>
                                                }
                                            />
                                            <Route
                                                path="/beehive/internal"
                                                element={
                                                    <AdminAuthGuard>
                                                        <InternalDashboardPage />
                                                    </AdminAuthGuard>
                                                }
                                            />
                                            <Route
                                                path="/beehive/buildstacks"
                                                element={
                                                    <Navigate
                                                        to="/hivecore/studio-v2"
                                                        state={{ from: '/beehive/buildstacks' }}
                                                        replace
                                                    />
                                                }
                                            />
                                            <Route
                                                path="/support/*"
                                                element={<SupportBridgeDemo />}
                                            />
                                            <Route
                                                path="/marketplace"
                                                element={
                                                    <Navigate
                                                        to="/hivecore/studio-v2"
                                                        state={{ from: '/marketplace' }}
                                                        replace
                                                    />
                                                }
                                            />
                                            <Route
                                                path="/buildlab/catalog"
                                                element={
                                                    <Navigate
                                                        to="/hivecore/studio-v2"
                                                        state={{ from: '/marketplace' }}
                                                        replace
                                                    />
                                                }
                                            />

                                            <Route
                                                path="/beehive/panels/branch-guard"
                                                element={<BranchGuardPanel />}
                                            />
                                            <Route
                                                path="/beehive/panels/supervisor"
                                                element={<SupervisorPanel />}
                                            />
                                            <Route
                                                path="/beehive/panels/arcgit"
                                                element={<ArcGitPanel />}
                                            />
                                            <Route
                                                path="/beehive/panels/assistant"
                                                element={<AssistantPanel />}
                                            />
                                            <Route
                                                path="/beehive/panels/repo-stats"
                                                element={<RepoStatsPanel />}
                                            />
                                            <Route
                                                path="/beehive/panels/scheduler"
                                                element={<SchedulerPanel />}
                                            />
                                            <Route
                                                path="/beehive/panels/playbooks"
                                                element={<PlaybooksPanel />}
                                            />
                                            <Route
                                                path="/beehive/panels/autofix"
                                                element={<AutoFixPanel />}
                                            />
                                            <Route
                                                path="/beehive/panels/dataexport"
                                                element={<DataExportPanel />}
                                            />
                                            <Route
                                                path="/beehive/panels/notification"
                                                element={<NotificationPanel />}
                                            />
                                            <Route
                                                path="/beehive/panels/annotations"
                                                element={<AnnotationPanel />}
                                            />
                                            <Route
                                                path="/beehive/oversight/*"
                                                element={<ArcOversightPlugin />}
                                            />

                                            <Route
                                                path="/beehive/support-bridge-demo"
                                                element={<SupportBridgeDemo />}
                                            />
                                            <Route
                                                path="/engineering-bridge"
                                                element={
                                                    <NativeCommsRBACProvider>
                                                        <EngineeringBridgePage />
                                                    </NativeCommsRBACProvider>
                                                }
                                            />

                                            <Route
                                                path="/beehive/control-bridge"
                                                element={<ControlBridgePanel />}
                                            />
                                            <Route
                                                path="/beehive/control-bridge/notifications/admin"
                                                element={<NotificationsAdminPanel />}
                                            />
                                            <Route
                                                path="/beehive/control-bridge/onboarding"
                                                element={<OnboardingProjectPicker />}
                                            />
                                            <Route
                                                path="/beehive/control-bridge/onboarding/:projectId"
                                                element={<OnboardingHubWrapper />}
                                            />

                                            <Route
                                                path="/control-hub/projects"
                                                element={<ProjectsList />}
                                            />
                                            <Route
                                                path="/control-hub/projects/:projectId"
                                                element={<ProjectsList />}
                                            />

                                            <Route
                                                path="/share/schedule/:token"
                                                element={<ScheduleLink />}
                                            />
                                            <Route
                                                path="/beehive/timing-test"
                                                element={<TimingTestPage />}
                                            />
                                            <Route
                                                path="/hivecore/smartgrid"
                                                element={<SmartGrid />}
                                            />
                                            <Route
                                                path="/hivecore/enhanced-smartgrid"
                                                element={<EnhancedSmartGridPage />}
                                            />
                                            <Route
                                                path="/hivecore/control"
                                                element={<HiveCoreControlRoute />}
                                            />
                                            <Route
                                                path="/hivecore"
                                                element={<HiveCoreDesktopRoute />}
                                            />
                                            <Route
                                                path="/hivecore/desktop"
                                                element={<HiveCoreDesktopRoute />}
                                            />
                                            <Route
                                                path="/hivecore/mobile"
                                                element={<HiveCoreMobileRoute />}
                                            />
                                            <Route
                                                path="/hivecore/studio"
                                                element={<HiveCoreStudioRoute />}
                                            />
                                            <Route
                                                path="/hivecore/studio-v2"
                                                element={<HiveCoreStudioRoute />}
                                            />
                                            <Route
                                                path="/hivecore/studio-v2/mission/:missionId"
                                                element={<HiveCoreStudioRoute />}
                                            />
                                            <Route
                                                path="/hivecore/studio-v2/app/:appId"
                                                element={<HiveCoreStudioRoute />}
                                            />
                                            <Route
                                                path="/hivecore/studio-v2/app/convops/:specId"
                                                element={<HiveCoreStudioRoute />}
                                            />
                                            <Route
                                                path="/hivecore/studio-v2/app/convops/:specId/runs/:runMissionId"
                                                element={<HiveCoreStudioRoute />}
                                            />
                                            <Route
                                                path="/hivecore/studio-v2/workspace/:workspaceId"
                                                element={<HiveCoreStudioRoute />}
                                            />
                                            <Route
                                                path="/hivecore/studio-v2/matrix"
                                                element={<HiveCoreStudioRoute />}
                                            />
                                            <Route
                                                path="/hivecore/studio-v2/run/:missionId"
                                                element={<HiveCoreStudioRoute />}
                                            />
                                            <Route
                                                path="/hivecore/studio-v2/missions/:builderMissionId"
                                                element={<HiveCoreStudioRoute />}
                                            />
                                            <Route
                                                path="/hivecore/studio-v2/decisions"
                                                element={<HiveCoreStudioRoute />}
                                            />
                                            {/* System Status - canonical route for operational truth surface */}
                                            <Route
                                                path="/hivecore/studio/system-status"
                                                element={<HiveCoreStudioRoute />}
                                            />
                                            <Route
                                                path="/hivecore/studio-v2/decisions/:decisionId"
                                                element={<HiveCoreStudioRoute />}
                                            />
                                            <Route
                                                path="/studio/builds"
                                                element={<HiveCoreStudioRoute />}
                                            />
                                            <Route
                                                path="/studio/builds/:buildId"
                                                element={<HiveCoreStudioRoute />}
                                            />

                                            <Route path="/wealthos" element={<WealthOSLayout />}>
                                                <Route
                                                    index
                                                    element={
                                                        <Navigate
                                                            to="/wealthos/dashboard"
                                                            replace
                                                        />
                                                    }
                                                />
                                                <Route
                                                    path="dashboard"
                                                    element={<WealthOSDashboard />}
                                                />
                                                <Route
                                                    path="smartgrid"
                                                    element={<WealthOSSmartGrid />}
                                                />
                                                <Route
                                                    path="reports"
                                                    element={<WealthOSReports />}
                                                />
                                                <Route path="forms" element={<WealthOSForms />} />
                                                <Route
                                                    path="oversight"
                                                    element={<WealthOSOversight />}
                                                />
                                                <Route
                                                    path="settings"
                                                    element={<WealthOSSettings />}
                                                />
                                            </Route>

                                            <Route path="/fitness-demo" element={<FitnessDemo />} />
                                            <Route
                                                path="/fitness-mobile"
                                                element={<FitnessMobile />}
                                            />
                                            <Route
                                                path="/fitness-desktop"
                                                element={<FitnessDesktop />}
                                            />
                                            <Route path="/realsignal" element={<RealSignalApp />} />
                                            <Route
                                                path="/realsignal/*"
                                                element={<RealSignalApp />}
                                            />
                                            <Route path="/mobile" element={<HiveCoreMobile />} />
                                            <Route path="/m" element={<HiveCoreMobile />} />

                                            {pluginRegistry.enabledPlugins.map(plugin => (
                                                <Route
                                                    key={plugin.manifest.id}
                                                    path={`/beehive${plugin.manifest.route}`}
                                                    element={
                                                        <DashboardLayout
                                                            activePluginId={plugin.manifest.id}
                                                        />
                                                    }
                                                />
                                            ))}

                                            <Route
                                                path="/beehive/plugins"
                                                element={<DashboardLayout />}
                                            />
                                        </>
                                    )}

                                    {!legacyEnabled && (
                                        <Route
                                            path="*"
                                            element={
                                                <Navigate to="/hivecore/workstation" replace />
                                            }
                                        />
                                    )}
                                </Routes>
                            </Router>
                            <Toaster
                                position="top-right"
                                toastOptions={{
                                    duration: 4000,
                                    style: {
                                        background: '#1e293b',
                                        color: '#f1f5f9',
                                        border: '1px solid #334155',
                                    },
                                    success: {
                                        iconTheme: {
                                            primary: '#10b981',
                                            secondary: '#f1f5f9',
                                        },
                                    },
                                    error: {
                                        iconTheme: {
                                            primary: '#ef4444',
                                            secondary: '#f1f5f9',
                                        },
                                    },
                                }}
                            />
                        </NotificationContext.Provider>
                    </ClientIsolationProvider>
                </DemoModeProvider>
            </ThemeProvider>
        </ErrorBoundary>
    );
}

export default App;
